/**
 * Razorpay SDK — true lazy loader.
 *
 * The script is NOT present in index.html. It is injected into the DOM only
 * when the user actually triggers a payment flow (i.e. when this function is
 * called). This eliminates the ~20 chunk requests Razorpay fires on every
 * page load when the script is loaded eagerly.
 *
 * The injection promise is cached at module scope, so no matter how many
 * components call waitForRazorpay() concurrently, the <script> tag is created
 * exactly once.
 *
 * @param {number} [timeoutMs=10000]
 * @returns {Promise<void>}
 */

const RAZORPAY_URL = 'https://checkout.razorpay.com/v1/checkout.js';

// Cached promise — null until first call, then reused forever.
let _loadPromise = null;

export function waitForRazorpay(timeoutMs = 10_000) {
  // ── Fast path: SDK already on window (e.g. hot-reload) ─────────────────
  if (window.Razorpay) return Promise.resolve();

  // ── Return cached in-flight / completed promise ─────────────────────────
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise((resolve, reject) => {
    // Double-check inside the promise executor (handles concurrent callers
    // that all passed the first fast-path check simultaneously).
    if (window.Razorpay) {
      resolve();
      return;
    }

    const timer = setTimeout(() => {
      reject(new Error('Razorpay SDK timed out. Please refresh the page and try again.'));
    }, timeoutMs);

    const cleanup = () => clearTimeout(timer);

    // Inject the <script> tag now — first and only time.
    const script = document.createElement('script');
    script.src = RAZORPAY_URL;
    script.async = true;

    script.addEventListener('load', () => {
      cleanup();
      if (window.Razorpay) {
        resolve();
      } else {
        // Give Razorpay one tick to assign window.Razorpay after load fires.
        setTimeout(() => {
          window.Razorpay
            ? resolve()
            : reject(new Error('Razorpay SDK loaded but window.Razorpay is undefined.'));
        }, 0);
      }
    }, { once: true });

    script.addEventListener('error', () => {
      cleanup();
      _loadPromise = null; // allow retry on next call
      reject(new Error('Razorpay SDK failed to load. Please check your connection and try again.'));
    }, { once: true });

    document.head.appendChild(script);
  });

  return _loadPromise;
}

/**
 * Dev-mock key returned by the backend when real Razorpay credentials
 * are not configured.  We detect this and bypass the Razorpay popup so
 * the entire payment flow can be tested locally without a real account.
 */
const DEV_MOCK_KEY = 'rzp_test_1234567890';

/**
 * Open the Razorpay checkout OR, when the backend is running in dev-mock
 * mode (key === DEV_MOCK_KEY), skip the popup and resolve immediately.
 *
 * Callers pass the same options object they would pass to `new window.Razorpay(...)`.
 * The returned promise resolves with a Razorpay payment response object:
 *   { razorpay_payment_id, razorpay_order_id, razorpay_signature }
 *
 * The backend must have MARKETPLACE_SKIP_PAYMENT_VERIFY=true (already set
 * via backend/.env) so it accepts the mock transactionId without verifying
 * the HMAC signature.
 *
 * @param {object} opts - Standard Razorpay options (key, amount, currency, order_id, …)
 * @returns {Promise<{razorpay_payment_id: string, razorpay_order_id: string, razorpay_signature: string}>}
 */
export async function openRazorpayCheckout(opts) {
  if (opts.key === DEV_MOCK_KEY) {
    // Dev / CI mock — never contacts Razorpay servers.
    console.log('🧪 Razorpay dev-mock: bypassing real checkout widget');
    return {
      razorpay_payment_id: `pay_mock_${Date.now()}`,
      razorpay_order_id:   opts.order_id,
      razorpay_signature:  'mock_sig_dev',
    };
  }

  // Real checkout path.
  await waitForRazorpay();
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      ...opts,
      handler: resolve,
      modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
    });
    rzp.on('payment.failed', (response) => {
      const errDesc   = response?.error?.description || 'Payment failed';
      const errReason = response?.error?.reason || '';
      reject(new Error(`Payment failed: ${errDesc}${errReason ? ` (${errReason})` : ''}`));
    });
    rzp.open();
  });
}
